import type { History } from "../document/history.ts";
import type { WorldDocument } from "../document/types.ts";
import { commandLabel } from "./command-labels.ts";

/**
 * The History drawer's list and jump (ADR-070's amendment "the drawers", decision 5). The history reads as one
 * line of commands: the done ones oldest first, then the undone ones in the order a redo brings them back. A
 * row's position counts the commands applied when the document stands just after it, from 0 for the start.
 * A jump walks there one step at a time through the store's own undo and redo, so each step does exactly what
 * the undo and redo keys do, map switches and the held-stroke refusal included.
 */

/**
 * One row: its position, its words, whether its command is applied now (`done`), waiting for a redo
 * (`undone`) or is the start, and whether the document stands at it.
 */
export interface HistoryRow {
  readonly position: number;
  readonly label: string;
  readonly state: "done" | "undone" | "start";
  readonly current: boolean;
}

/**
 * What a jump steps: the undo depth and the store's undo and redo, each false when it did nothing.
 */
export interface HistoryStepper {
  readonly undoDepth: number;
  readonly undo: () => boolean;
  readonly redo: () => boolean;
}

/**
 * The rows, newest first: the undone commands, then the done ones, then the start, which reads "Opened" or,
 * once the history's cap has let older commands go, "Oldest change kept".
 *
 * @param history The store's history.
 * @param dropped How many commands the cap has let go.
 * @param document The document now, which names layers in the words.
 * @returns The rows.
 */
export function historyRows(history: History, dropped: number, document: WorldDocument): readonly HistoryRow[] {
  const line = [...history.past, ...[...history.future].reverse()];
  const at = history.past.length;
  const rows: HistoryRow[] = line.map((command, i) => ({ position: i + 1, label: commandLabel(command, document), state: i < at ? "done" : "undone", current: i + 1 === at }));
  rows.reverse();
  rows.push({ position: 0, label: dropped > 0 ? "Oldest change kept" : "Opened", state: "start", current: at === 0 });
  return rows;
}

/**
 * Undoes or redoes to a row's position one step at a time, stopping where a step is refused.
 *
 * @param stepper The store, or anything that steps as it does.
 * @param position The row's position.
 * @returns How many steps were taken.
 */
export function jumpHistory(stepper: HistoryStepper, position: number): number {
  let steps = 0;
  while (stepper.undoDepth > position && stepper.undo()) {
    steps += 1;
  }
  while (stepper.undoDepth < position && stepper.redo()) {
    steps += 1;
  }
  return steps;
}
